// Buildings (PLAN §14): what may be built, where, and what happens when one
// comes down.
//
// **A structure stands on a cell** (§31 S1). Its kind's row in
// `STRUCTURE_ROWS` says whether it is an **Obstruction** — a wall to every
// non-jumping move and to line of sight, attacked where it stands — or
// plowable: an enemy mover destroys it in passing (`chess/plow.ts`), anyone
// may stand on one of their own side's. Movegen reads the position's mirror
// (`structures`, `structureHealth`, `obstructions`), which this file writes.
//
// Perft is the proof that this leaked nowhere: a classic board has no
// structures, and every emitter tests the mirror's size first.

import { isTurretKind, newTurret, turretEmplaced } from "./turret";
import {
  type Color,
  fileOf,
  pieceColor,
  rankOf,
  square as squareOf
} from "../board";
import {
  hasCell,
  invalidateMoves,
  pieceAt,
  type Position,
  setCells,
  setObstruction,
  setStructureHealth,
  setStructureOwner
} from "../position";
import { type Grade, POWER, RULES, structureRow, STRUCTURE_ROWS, inventoryOf } from "../rules";
import {
  ACCEPT_ALL,
  addHeap,
  clearDeposit,
  bufferAtLeast,
  deliverUnit,
  offersGrade,
  structureSquares,
  droppedAt,
  takeGroundOf,
  dropOnGround,
  hasBelt,
  hasBlueprint,
  heapAt,
  heapSideAt,
  type IndustryState,
  makeUnit,
  pushUnit,
  setStructure,
  STORE,
  type Structure,
  type StructureKind,
  structureAt,
  takeUnitOf,
  TURRET,
  ALTAR,
  RELIC,
  KEYSTONE,
  WELL,
  SIMPLE_WALL,
  FIRM_WALL,
  REINFORCED_WALL,
  ARMORY,
  COAL_GENERATOR,
  NUCLEAR_GENERATOR,
  BATTERY,
  CABLING_TOWER,
  TRACK,
  TRAIN_CAR,
  TRAIN_ENGINE,
  FUSION_CHAMBER,
  SHIELD_GENERATOR,
  ARTISAN_PILLAR,
  KINETIC_TURRET,
  CANNON_TURRET,
  PROPAGANDA_TOWER,
  HOUSE_OF_CARDS,
  ENDURANCE_MONUMENT,
  FIEND_FOUNTAIN,
  LEGION_TOWER,
  DRONE_WAREHOUSE,
  EMPOWER_TOWER,
  unitCount,
  unitMeets,
  unitValue,
  unitGrade,
  unitLine,
  GRADE_SLOTS
} from "./state";
import { type Bill, FUEL, LINE_COUNT, SPECIAL, STONE } from "../materials";
import { isFoundryAt, loadTarget } from "./machines";
import { syncBeltMirror } from "./beltParts";
import { couplingFor, newTrack, releaseTrain, standTrain } from "./trains";
import type { DemandCache } from "./routing";

/** Every kind that can stand on the board, in kind order. Retired 4 and 7 are not. */
export const STRUCTURE_KINDS: readonly StructureKind[] = [
  STORE,
  TURRET,
  ALTAR,
  RELIC,
  KEYSTONE,
  WELL,
  SIMPLE_WALL,
  FIRM_WALL,
  REINFORCED_WALL,
  ARMORY,
  COAL_GENERATOR,
  NUCLEAR_GENERATOR,
  BATTERY,
  CABLING_TOWER,
  TRACK,
  TRAIN_ENGINE,
  TRAIN_CAR,
  FUSION_CHAMBER,
  SHIELD_GENERATOR,
  ARTISAN_PILLAR,
  KINETIC_TURRET,
  CANNON_TURRET,
  PROPAGANDA_TOWER,
  HOUSE_OF_CARDS,
  ENDURANCE_MONUMENT,
  FIEND_FOUNTAIN,
  LEGION_TOWER,
  DRONE_WAREHOUSE,
  EMPOWER_TOWER
];

/** What the build flyout offers, in order: the store, the three turrets (§31 S8), then §31 S4–S7. */
export const BUILD_KINDS: readonly StructureKind[] = [
  STORE,
  // §33 I5: the store's upgrade, beside it.
  DRONE_WAREHOUSE,
  TURRET,
  KINETIC_TURRET,
  CANNON_TURRET,
  SIMPLE_WALL,
  FIRM_WALL,
  REINFORCED_WALL,
  ARMORY,
  COAL_GENERATOR,
  NUCLEAR_GENERATOR,
  BATTERY,
  CABLING_TOWER,
  TRACK,
  TRAIN_ENGINE,
  TRAIN_CAR,
  // §33 S1.
  EMPOWER_TOWER
];

const perKind = <T>(of: (kind: StructureKind) => T): Record<StructureKind, T> =>
  Object.fromEntries(STRUCTURE_KINDS.map((kind) => [kind, of(kind)])) as Record<StructureKind, T>;

/**
 * The first bill row's count and grade — the scalar a store's readers quote.
 * `STRUCTURE_ROWS[kind].bill` is the whole price (§31 S4–S6); the artifact
 * kinds and the turret keep the store's.
 */
export const STRUCTURE_COST: Record<StructureKind, number> = perKind(
  (kind) => STRUCTURE_ROWS[kind].bill[0].count
);

export const STRUCTURE_GRADE: Record<StructureKind, Grade> = perKind(
  (kind) => STRUCTURE_ROWS[kind].bill[0].grade as Grade
);

/** What it takes to knock each kind down — `STRUCTURE_ROWS`' health (§31 S1, S4–S6). */
export const STRUCTURE_HEALTH: Record<StructureKind, number> = perKind(
  (kind) => STRUCTURE_ROWS[kind].health
);

/** `build` takes this kind (§31 S3–S6): the store and every §31 S kind. */
export const isBuildableKind = (kind: number): kind is StructureKind =>
  STRUCTURE_ROWS[kind]?.buildable === true;

/**
 * Has no taps and takes no deliveries: everything but a store, a turret
 * (its ammunition alone, §31 S8) and a Coal Generator (coal alone,
 * `canHoldUnit`).
 */
export const isSealedKind = (kind: StructureKind): boolean =>
  !isStockpileKind(kind) && !isTurretKind(kind) && !structureRow(kind).burnsCoal;

/**
 * A stockpile (§33 I2): the store, the Armory, the Train Car — taps, routing
 * fills it, builds and upgrades pay from it. Its row says so.
 */
export const isStockpileKind = (kind: StructureKind): boolean => structureRow(kind).stockpile === true;

/** Is this kind an **Obstruction** (§31 S1)? `STRUCTURE_ROWS` decides. */
export const isObstructionKind = (kind: StructureKind): boolean => structureRow(kind).obstruction;

/**
 * Is this structure an obstruction — its kind's row, or a turret emplaced on
 * a Reinforced Wall (§31 S8: the wall stays under it).
 */
export const isObstruction = (structure: Structure): boolean =>
  isObstructionKind(structure.kind) || turretEmplaced(structure);

/** Takes no ranged damage: Firm and Reinforced Walls (S4), an emplaced turret (S8). */
export const isRangedImmune = (structure: Structure): boolean =>
  structureRow(structure.kind).rangedImmune === true || turretEmplaced(structure);

/** Its whole health: the row's, plus a Reinforced Wall's under an emplaced turret (§31 S8). */
export const structureMaxHealth = (structure: Structure): number =>
  STRUCTURE_HEALTH[structure.kind] +
  (turretEmplaced(structure) ? STRUCTURE_ROWS[REINFORCED_WALL].health : 0) +
  // Yield Not's bulwark while on (§31 A12).
  (structure.bulwark ?? 0);

/**
 * Health this building has left.
 *
 * **The one reading of `Structure.health`**, which is optional: the codec is
 * structural, so a run saved before buildings could be hurt decodes without
 * the field, and an undamaged building is the honest reading of that. Every
 * caller goes through here rather than through `?? STRUCTURE_HEALTH[...]`
 * spelled out again, which is the same defaulting drifting in four places.
 */
export const structureHealthOf = (structure: Structure): number =>
  Math.min(structure.health ?? structureMaxHealth(structure), structureMaxHealth(structure));

/**
 * Take a blow off a building's health, in the industry's record alone.
 *
 * **The industry half of a structure bash**, `wreckStructure`'s twin and split
 * from the position for the same reason: `legalMoves` makes and unmakes every
 * candidate through `makeMove`, which writes the mirror, and a search must not
 * grind a real warehouse down by thinking about hitting it. `applyMove` calls
 * this once, for the move actually played.
 *
 * It cannot reach nought — that is what made the blow a bash rather than a
 * capture — but it is clamped anyway rather than trusting a caller.
 */
export function hurtStructure(industry: IndustryState, at: number, damage: number): void {
  const structure = structureAt(industry, at);
  if (!structure) return;
  structure.health = Math.max(1, structureHealthOf(structure) - damage);
}

/**
 * The line a building is made of: **stone**, in both arms (§26 F2's text,
 * turned on by F3 giving stone a source). One constant rather than a field on
 * `Structure`, because every kind is stone and a stored line would be a second
 * thing to keep in step — and it would move the hash of every board that has
 * ever had a warehouse on it.
 */
export const STRUCTURE_LINE = STONE;

/** The build bill, the design's table (`STRUCTURE_ROWS`, §31 S4–S6). */
export function structureBill(kind: StructureKind): Bill {
  return structureRow(kind).bill;
}

export const STRUCTURE_NAMES: Record<StructureKind, string> = {
  [STORE]: "Store",
  [TURRET]: "Laser Turret",
  [ALTAR]: "Scrap Altar",
  [RELIC]: "Titan Relic",
  [KEYSTONE]: "Mainframe",
  [WELL]: "Sacred Well",
  [SIMPLE_WALL]: "Simple Wall",
  [FIRM_WALL]: "Firm Wall",
  [REINFORCED_WALL]: "Reinforced Wall",
  [ARMORY]: "Armory",
  [COAL_GENERATOR]: "Coal Generator",
  [NUCLEAR_GENERATOR]: "Nuclear Generator",
  [BATTERY]: "Industrial Battery",
  [CABLING_TOWER]: "Power Cabling Tower",
  [TRACK]: "Train Track",
  [TRAIN_ENGINE]: "Train Engine",
  [TRAIN_CAR]: "Train Car",
  [FUSION_CHAMBER]: "Fusion Chamber",
  [SHIELD_GENERATOR]: "Shield Generator",
  [ARTISAN_PILLAR]: "Pillar of Excellence",
  [KINETIC_TURRET]: "Kinetic Turret",
  [CANNON_TURRET]: "Cannon Turret",
  [PROPAGANDA_TOWER]: "Propaganda Tower",
  [HOUSE_OF_CARDS]: "House of Cards",
  [ENDURANCE_MONUMENT]: "Endurance Monument",
  [FIEND_FOUNTAIN]: "Fountain of Fiendishness",
  [LEGION_TOWER]: "Legion Tactics Tower",
  [DRONE_WAREHOUSE]: "Drone-Assisted Warehouse",
  [EMPOWER_TOWER]: "Empower Tower"
};

/**
 * The eight squares around a piece, in a fixed order.
 *
 * Cardinals before diagonals, exactly like `MINT_ORDER`, because two clients
 * that take "the first one" have to take the same one (PLAN §5.5).
 */
const AROUND = [
  { df: 0, dr: 1 },
  { df: 1, dr: 0 },
  { df: 0, dr: -1 },
  { df: -1, dr: 0 },
  { df: 1, dr: 1 },
  { df: 1, dr: -1 },
  { df: -1, dr: 1 },
  { df: -1, dr: -1 }
] as const;

export const neighbours = (sq: number): number[] => {
  const file = fileOf(sq);
  const rank = rankOf(sq);
  return AROUND.map(({ df, dr }) => squareOf(file + df, rank + dr));
};

/** Is this piece standing next to that square — diagonals included? */
export const touches = (a: number, b: number): boolean =>
  a !== b &&
  Math.abs(fileOf(a) - fileOf(b)) <= 1 &&
  Math.abs(rankOf(a) - rankOf(b)) <= 1;

/**
 * The first altar of this side's standing beside a square, or -1 (§26 A1).
 *
 * All eight neighbours, `touches`' reading — the designer's "on or adjacent"
 * has no "on", since a piece can never stand on a structure's square, and
 * eight-way is what "adjacent" means everywhere a player meets it (territory,
 * a knight's re-aim, F7's craft spending from touching foundries).
 * Deliberately *not* `materialLinks`, whose cardinal-only structure rule is
 * about automatic hand-offs; the sacrifice and the capture bonus are acts,
 * not routing. Sorted squares, so two clients that take "the first one" take
 * the same one — and a victim beside two altars yields **once**, into this
 * one, because "per altar" (§26.6 9) means location-gated, never stacked.
 */
export function altarBeside(industry: IndustryState, side: Color, sq: number): number {
  for (const at of structureSquares(industry)) {
    const structure = structureAt(industry, at)!;
    if (structure.kind !== ALTAR || structure.owner !== side) continue;
    if (touches(at, sq)) return at;
  }
  return -1;
}

/**
 * Put a unit into a structure's stock, capped — the writer sacrifice and the
 * altar's capture bonus share (§26 A1).
 *
 * Never `deliverUnit`: that door asks the accept mask, which an artifact kind
 * keeps at 0 precisely so the routing field cannot court it. This is the
 * furnace writing its own output, the way a refinery writes its `out` bank.
 */
export function claimStock(structure: Structure, unit: number): boolean {
  if (structure.stock.length >= inventoryOf(structure.kind)) return false;
  structure.stock.push(unit);
  return true;
}

/** What an `over` row's missing foundation is called. */
const overError = (over: number): BuildError => (over === STORE ? "needs-store" : "needs-firm-wall");

export type BuildError =
  | "not-your-piece"
  | "too-poor"
  | "no-room"
  | "not-beside-you"
  | "square-not-empty"
  // A Reinforced Wall goes only over a Firm Wall of yours (§31 S4).
  | "needs-firm-wall"
  // A Drone-Assisted Warehouse goes only over a store of yours (§33 I5).
  | "needs-store"
  // An upgrade site (§31 S5): no Armory of yours stands …
  | "no-armory"
  // … or the site is outside every Armory's reach and the builder forges nothing.
  | "out-of-armory-reach"
  // A train goes up over an own Track only (§31 S7) …
  | "needs-track"
  // … and a car only where it couples to an own engine's tail.
  | "no-coupling";

/** Chebyshev distance. */
const chebyshev = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/**
 * Does this kind work as an Armory — the Armory itself, or the Fusion Chamber
 * (§31 A8: "acts as an armory")? The building's function, not the
 * artifact's: it needs no Signet, as the Sacred Well generates without its
 * Scepter (A4). Fusing is the artifact's (`campaign/fusion.ts`).
 */
export const actsAsArmory = (kind: StructureKind): boolean => kind === ARMORY || kind === FUSION_CHAMBER;

/** Does `owner` have an Armory (or a Fusion Chamber) standing (§31 S5, A8)? */
export function hasArmory(industry: IndustryState, owner: Color): boolean {
  for (const structure of industry.structures.values()) {
    if (actsAsArmory(structure.kind) && structure.owner === owner) return true;
  }
  return false;
}

/** Is `at` within `POWER.armoryReach` of an Armory (or Fusion Chamber) of `owner`'s (§31 S5, A8)? */
export function armoryCovers(industry: IndustryState, owner: Color, at: number): boolean {
  for (const sq of structureSquares(industry)) {
    const structure = structureAt(industry, sq)!;
    if (actsAsArmory(structure.kind) && structure.owner === owner && chebyshev(sq, at) <= POWER.armoryReach) {
      return true;
    }
  }
  return false;
}

/**
 * **The upgrade site** (§31 S5, design "Armory"): an Armory of yours must
 * stand at all, and `at` is within 2 of one or beside a forge-capable piece
 * of yours (`forgerBeside`). Shared by `upgrade` and the Reinforced Wall.
 */
export function upgradeSiteError(
  industry: IndustryState,
  owner: Color,
  at: number,
  forgerBeside: boolean
): "no-armory" | "out-of-armory-reach" | null {
  if (!hasArmory(industry, owner)) return "no-armory";
  if (forgerBeside || armoryCovers(industry, owner, at)) return null;
  return "out-of-armory-reach";
}

/**
 * A square is **bare** — nothing on it at all.
 *
 * The list is every Map keyed by a square, and it is exhaustive on purpose: a
 * square that leaves the board with an entry still on it is an orphan that
 * nothing sweeps, that no piece can ever reach, and that is in the hash for
 * the rest of the game. Black Hole refuses on the same list for the same
 * reason.
 */
export function isBare(
  position: Position,
  industry: IndustryState,
  sq: number,
  /**
   * Squares a level needs to stay walkable, if this is a run.
   *
   * A "reach" objective is a square you have to *stand on*, and an
   * obstruction cannot be stood on — so walling one is a level that
   * can never be finished, with its objective still outstanding and no way to
   * undo it but to spend a turn taking the building down. A siege of somebody
   * else's king is a fair use of that; sealing your own win condition is not a
   * decision anybody makes on purpose.
   */
  reserved: readonly number[] = []
): boolean {
  if (reserved.includes(sq)) return false;
  return (
    hasCell(position, sq) &&
    pieceAt(position, sq) === 0 &&
    !hasBelt(industry, sq) &&
    !hasBlueprint(industry, sq) &&
    !structureAt(industry, sq) &&
    heapAt(industry, sq) === 0 &&
    heapSideAt(industry, sq) === -1 &&
    droppedAt(industry, sq).length === 0 &&
    unitCount(industry, sq, "cargo") === 0 &&
    unitCount(industry, sq, "in") === 0 &&
    unitCount(industry, sq, "out") === 0 &&
    !position.promotion.has(sq)
  );
}

/**
 * Does `owner` control a structure that lifts the adjacency rule (S3's hook
 * for the design's unnamed "special high cost structure")? No kind does yet;
 * the row field `liftsAdjacency` is where one will say so.
 */
export function liftsAdjacency(industry: IndustryState, owner: Color): boolean {
  for (const structure of industry.structures.values()) {
    if (structure.owner === owner && structureRow(structure.kind).liftsAdjacency) return true;
  }
  return false;
}

/**
 * May `kind` go up on `at` for `owner` — the square alone (§31 S4)? Bare
 * ground, or an own wall it is built over: a Reinforced Wall only over a Firm
 * one (`over`), a Firm Wall over bare ground or a Simple one (`replaces`).
 */
export function siteTakes(
  position: Position,
  industry: IndustryState,
  at: number,
  owner: Color,
  kind: StructureKind,
  reserved: readonly number[] = []
): boolean {
  const row = structureRow(kind);
  const there = structureAt(industry, at);
  // A train stands on an own Track and nothing else (§31 S7) — no piece on it.
  if (row.onTrack) return there?.kind === TRACK && there.owner === owner && pieceAt(position, at) === 0;
  if (row.over !== undefined) return there?.kind === row.over && there.owner === owner;
  if (row.replaces !== undefined && there?.kind === row.replaces && there.owner === owner) return true;
  return isBare(position, industry, at, reserved);
}

/** Every square beside this piece `kind` could go on, in a fixed order. */
export function buildSquares(
  position: Position,
  industry: IndustryState,
  by: number,
  owner: Color,
  reserved: readonly number[] = [],
  kind: StructureKind = STORE
): number[] {
  const armory = structureRow(kind).needsArmory === true;
  const forges = armory && isFoundryAt(position, industry, by);
  return neighbours(by).filter(
    (sq) =>
      siteTakes(position, industry, sq, owner, kind, reserved) &&
      (kind !== TRAIN_CAR || couplingFor(industry, sq, owner) >= 0) &&
      (!armory || upgradeSiteError(industry, owner, sq, forges) === null)
  );
}

/** Chebyshev distance — what "nearest stockpile" measures. */
const away = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/**
 * `owner`'s stores that hold stock, nearest `near` first, ties by square —
 * the order a building's bill is drawn from after the paying piece (§31 S3).
 * Deterministic, so two clients take the same units.
 */
export function storesNearest(industry: IndustryState, owner: Color, near: number): number[] {
  return structureSquares(industry)
    .filter((sq) => {
      const one = structureAt(industry, sq)!;
      return isStockpileKind(one.kind) && one.owner === owner && one.stock.length > 0;
    })
    .sort((a, b) => away(a, near) - away(b, near) || a - b);
}

/**
 * The squares whose **loose ground** pays a build on `site` (§31 Q13): the
 * site, then the eight touching it, by square. Ground is anybody's, and it
 * is to hand — what lets the versus opening's cobble rows build at once.
 */
export const groundNear = (site: number): number[] => [
  site,
  ...neighbours(site).sort((a, b) => a - b)
];

/** Loose units near `site` meeting one bill row. */
export function groundFunds(
  industry: IndustryState,
  site: number,
  row: { line: number; grade: number }
): number {
  let total = 0;
  for (const sq of groundNear(site)) {
    for (const unit of droppedAt(industry, sq)) {
      if (unitMeets(unit, row.line as 0, row.grade)) total++;
    }
  }
  return total;
}

/** Spend one unit of a bill row off the ground near `site`, cheapest first. */
export function takeGroundNear(
  industry: IndustryState,
  site: number,
  row: { line: number; grade: number }
): boolean {
  for (const sq of groundNear(site)) {
    if (takeGroundOf(industry, sq, row.line as 0, row.grade) >= 0) return true;
  }
  return false;
}

/**
 * How much of one bill row `owner` can pay for a build by `by` near `near`:
 * the piece's inbox, then loose ground near the site (§31 Q13), then every
 * store of theirs (§31 S3).
 */
export function buildFunds(
  industry: IndustryState,
  by: number,
  owner: Color,
  row: { line: number; grade: number },
  near: number
): number {
  let total = bufferAtLeast(industry, by, row.grade as Grade, "in", row.line as 0);
  total += groundFunds(industry, near, row);
  for (const sq of storesNearest(industry, owner, near)) {
    for (const unit of structureAt(industry, sq)!.stock) {
      if (unitMeets(unit, row.line as 0, row.grade)) total++;
    }
  }
  return total;
}

/**
 * Why `owner` cannot build `kind` from the piece on `by` right now, or null
 * (§31 S3). **Any piece of yours** is a builder: the square must be bare and
 * beside it (unless `liftsAdjacency`), and the bill is paid from that piece's
 * inbox first, then your stores nearest the site. Costs no move.
 */
export function canBuild(
  position: Position,
  industry: IndustryState,
  by: number,
  owner: Color,
  kind: StructureKind,
  at?: number,
  /** Squares a level needs walkable. See `isBare`. */
  reserved: readonly number[] = []
): BuildError | null {
  const piece = pieceAt(position, by);
  if (!piece || pieceColor(piece) !== owner) return "not-your-piece";
  // Row by row, the bill's own shape.
  for (const row of structureBill(kind)) {
    if (buildFunds(industry, by, owner, row, at ?? by) < row.count) return "too-poor";
  }

  const row = structureRow(kind);
  // An upgrade site needs an Armory standing at all (§31 S4, S5).
  if (row.needsArmory && !hasArmory(industry, owner)) return "no-armory";

  const lifted = liftsAdjacency(industry, owner);
  // Asked without a site, the question is whether any site exists; with one,
  // the site's own refusals below say more.
  if (at === undefined) {
    if (!lifted && buildSquares(position, industry, by, owner, reserved, kind).length === 0) {
      if (kind === TRAIN_CAR && neighbours(by).some((sq) => siteTakes(position, industry, sq, owner, kind, reserved))) {
        return "no-coupling";
      }
      return row.onTrack ? "needs-track" : row.over !== undefined ? overError(row.over) : "no-room";
    }
    return null;
  }

  if (!lifted && !touches(by, at)) return "not-beside-you";
  if (!siteTakes(position, industry, at, owner, kind, reserved)) {
    return row.onTrack ? "needs-track" : row.over !== undefined ? overError(row.over) : "square-not-empty";
  }
  if (kind === TRAIN_CAR && couplingFor(industry, at, owner) < 0) return "no-coupling";
  if (row.needsArmory) {
    const why = upgradeSiteError(industry, owner, at, isFoundryAt(position, industry, by));
    if (why) return why;
  }
  return null;
}

/**
 * Put the building up. The caller has checked `canBuild`.
 *
 * The square leaves the board through `setCells` — never by writing `cells`
 * alone, which would leave `bounds` and `cellList` describing the board as it
 * used to be and every sweep carrying on as though the ground were still there.
 */
/**
 * What a building is the moment it exists, and nothing about paying for it.
 *
 * Split out because a level stands buildings up without a foundry, without
 * material and without a turn — `openRegion` places what a room authored, the
 * same way it places pieces and heaps. Every field here is easy to get subtly
 * wrong the second time it is written: `paidGrade` decides what demolishing
 * refunds, `accept` decides whether a fresh store is useful at all, and a
 * turret with no stats is a gun that cannot fire.
 */
export function newStructure(kind: StructureKind, owner: Color): Structure {
  return {
    kind,
    owner,
    stock: [],
    // Useful the moment it exists. A store that emitted by default would walk
    // its contents off toward whatever machine was nearest, which is precisely
    // the complaint the `hold` switch already answers.
    //
    // **An artifact structure is sealed** (§26 F6): it is a power's address,
    // not a warehouse, so the routing field must not court it with deliveries.
    // A wall, the Armory and the power network are sealed too (§31 S4–S6);
    // a Coal Generator opens every grade and `canHoldUnit` admits coal alone.
    accept: isSealedKind(kind) ? 0 : ACCEPT_ALL,
    // **The altar is born offering, and it has no knobs** (§26 A1). Sacrifice
    // and the capture bonus land material *in* its stock, and a stock nothing
    // ever emits is material that silently left the game — so `runStores`
    // hands it on from the day the building stands. A furnace, not a
    // warehouse: `accept` stays 0 so the routing field never courts it, and
    // `set-store-policy` still refuses the kind, so neither mask is a setting.
    offer: kind === ALTAR ? ACCEPT_ALL : 0,
    paidGrade: STRUCTURE_GRADE[kind],
    // Whole (§27). Ten for every kind today, and the table is where a kind
    // that should be tougher says so.
    health: STRUCTURE_HEALTH[kind],
    // A turret starts a full round, no fuel, facing its side's forward (§31 S8).
    turret: isTurretKind(kind) ? newTurret(kind, owner) : undefined
  };
}

/**
 * Stand a structure on a square: the industry's record, the position's owner
 * mirror, and the square leaving `cells`, together.
 *
 * **The one writer for "a building goes up"** (§26 F5). The mirror is what
 * makes an enemy structure a capture target to move generation, which is
 * handed a `Position` and nothing else — so a building recorded in the
 * industry alone is a wall nothing can ever take, silently. Every placement
 * path goes through here: `build`, a level's rooms, a camp's seam.
 */
export function standStructure(
  position: Position,
  industry: IndustryState,
  at: number,
  structure: Structure
): void {
  setStructure(industry, at, structure);
  setStructureOwner(position, at, structure.owner);
  // The health mirror goes up with the owner one, or movegen reads nought and
  // every blow at a brand-new warehouse takes it in one.
  setStructureHealth(position, at, structureHealthOf(structure));
  setObstruction(position, at, isObstruction(structure));
  // **It stands on a cell** (§31 S1). A placement authored off the floor
  // makes the floor, rather than leaving a building on the void.
  if (!hasCell(position, at)) {
    const cells = new Set(position.cells);
    cells.add(at);
    setCells(position, cells);
  }
}

export function buildStructure(
  position: Position,
  industry: IndustryState,
  foundry: number,
  owner: Color,
  kind: StructureKind,
  at: number
): void {
  // The piece's inbox first, cheapest first, then loose ground near the site
  // (§31 Q13), then the stores nearest it (§31 S3). The same order
  // `buildFunds` counted, or a build passes the door and then cannot be paid.
  for (const row of structureBill(kind)) {
    for (let i = 0; i < row.count; i++) {
      if (takeUnitOf(industry, foundry, row.line, row.grade as Grade) >= 0) continue;
      if (takeGroundNear(industry, at, row)) continue;
      for (const sq of storesNearest(industry, owner, at)) {
        if (takeStockOf(structureAt(industry, sq)!, row.line, row.grade)) break;
      }
    }
  }

  // **Concrete buries the seam.** Paving over ore is the player's call — the
  // build is not refused — and nobody mines through a floor.
  clearDeposit(industry, at);
  // **Built over a wall** (§31 S4): the old one is consumed, no refund — an
  // upgrade, not a dismantle. The new one stands whole.
  // A train (§31 S7) consumes the Track into itself: it is implied beneath.
  const under = structureAt(industry, at);
  if (under) setStructure(industry, at, null);
  const structure = newStructure(kind, owner);
  // **A warehouse over a store keeps it** (§33 I5): its stock and its taps.
  if (under?.kind === STORE && isStockpileKind(kind)) {
    structure.stock = under.stock.slice(0, inventoryOf(kind));
    structure.accept = under.accept;
    structure.offer = under.offer;
  }
  // **An emplacement** (§31 S8): a turret over an own Reinforced Wall.
  if (structure.turret && under?.kind === REINFORCED_WALL) {
    structure.turret.emplaced = true;
    structure.health = structureMaxHealth(structure);
  }
  if (structureRow(kind).onTrack) standTrain(industry, at, structure);
  standStructure(position, industry, at, structure);
}

/** Spend the cheapest unit of a store's stock that meets a bill row. */
export function takeStockOf(structure: Structure, line: number, grade: number): boolean {
  let best = -1;
  let bestValue = Infinity;
  for (let i = 0; i < structure.stock.length; i++) {
    const unit = structure.stock[i];
    if (!unitMeets(unit, line as 0, grade)) continue;
    const value = unitValue(unit);
    if (value < bestValue) {
      bestValue = value;
      best = i;
    }
  }
  if (best < 0) return false;
  structure.stock.splice(best, 1);
  return true;
}

/**
 * Take it down, and put the square back.
 *
 * Who did it decides what is left:
 *
 *  - **The owner dismantles it** and gets half the build cost back at the
 *    grade it was paid in, along with everything inside it, intact. Taking a
 *    thing apart on purpose is not the same as having it wrecked.
 *  - **An enemy wrecks it** and the stock spills as grade-0 scrap on the
 *    square — the settled rule that anything destroyed while carrying material
 *    spills it as scrap. A store holding forty diamond is forty iron on the
 *    floor the moment somebody knocks it down, and both players know it.
 *
 * What cannot fit in the dismantler goes on the ground intact, where any piece
 * ending its move there picks it up — the same route a lifted belt's cargo
 * takes.
 */
export function demolishStructure(
  position: Position,
  industry: IndustryState,
  by: number,
  at: number
): void {
  const structure = structureAt(industry, at);
  if (!structure) return;

  const piece = pieceAt(position, by);
  const mine = piece !== 0 && pieceColor(piece) === structure.owner;

  if (mine) {
    // Every bill row, at its own line and rung (§31 S4–S6). The line as well
    // as the grade: `makeUnit` defaults to metal, so a refund that named only
    // the rung would hand back iron for cobble.
    for (const row of structureBill(structure.kind)) {
      const back = Math.floor(row.count * RULES.dismantleRecovery);
      for (let i = 0; i < back; i++) {
        const unit = makeUnit(row.grade, 0, row.line);
        if (!pushUnit(industry, by, unit, "in")) dropOnGround(industry, at, unit);
      }
    }
    for (const unit of structure.stock) {
      if (!pushUnit(industry, by, unit, "in")) dropOnGround(industry, at, unit);
    }
  } else {
    // Grade is what is lost, never quantity — the same arithmetic a spilled
    // piece takes. `addHeap` because scrap on the floor is a count, not units.
    if (structure.stock.length > 0) addHeap(industry, at, structure.stock.length);
  }

  setStructure(industry, at, null);
  setStructureOwner(position, at, null);
  setStructureHealth(position, at, 0);
  setObstruction(position, at, false);
  // A train (§31 S7) uncouples; its own dismantle hands the Track back, an
  // enemy's takes it with the train.
  if (structureRow(structure.kind).onTrack) {
    releaseTrain(industry, at, structure);
    if (mine) standStructure(position, industry, at, newTrack(structure.owner));
  }
}

/**
 * The industry's half of a structure being **captured** (§26 F5).
 *
 * `makeMove` has already put the square back in `cells` and stood the
 * capturer on it; this is what the building leaves behind, called from
 * `applyMove` before the move is made — the split that keeps a search from
 * wrecking a real warehouse while merely considering the capture.
 *
 * What is left is a wreck, not a refund, and the two halves take the two
 * readings a death already has: the **stock spills as grade-0 scrap** — the
 * settled rule that grade does not survive its carrier, and exactly what an
 * enemy demolishing it leaves — and the **fabric drops as cobble**, the
 * building's own line at grade 0, the way a piece's body becomes metal scrap.
 * Full build cost in raw stone is strictly less value than the build bill
 * (grade 1), so capture-then-rebuild can never be a pump — the same invariant
 * `mintCost > PIECE_VALUE` pins for pieces, and a test pins here.
 */
export function wreckStructure(
  position: Position,
  industry: IndustryState,
  at: number
): void {
  const structure = structureAt(industry, at);
  if (!structure) return;
  if (structure.stock.length > 0) addHeap(industry, at, structure.stock.length);
  // A train (§31 S7) uncouples, its riders die with it, and the Track
  // beneath goes with it — its fabric drops too.
  const onTrack = structureRow(structure.kind).onTrack === true;
  if (onTrack) releaseTrain(industry, at, structure);
  const bill = onTrack ? [...structureBill(structure.kind), ...structureBill(TRACK)] : structureBill(structure.kind);
  // Each stone and metal row drops as its line's grade 0; specials and fuel
  // are consumed (§31 S4–S6, A4 — a Well's coal is burned in). Below the
  // bill, so never a pump.
  for (const row of bill) {
    if (row.line === SPECIAL || row.line === FUEL) continue;
    for (let i = 0; i < row.count; i++) dropOnGround(industry, at, makeUnit(0, 0, row.line));
  }
  setStructure(industry, at, null);
  // The mirror is usually already clear — `makeMove` cleared it — but the
  // industry must be able to wreck one without a move having happened. Both
  // halves of it: a health entry left behind is a wound the next piece to
  // stand here inherits.
  setStructureOwner(position, at, null);
  setStructureHealth(position, at, 0);
  setObstruction(position, at, false);
}

/**
 * Rebuild the position's owner mirror from the industry's record.
 *
 * For a state that arrived from **outside the reducer**: a run saved before
 * the mirror existed decodes to a position without it, and a fixture built by
 * hand may have written the industry alone. Derived state gets rebuilt rather
 * than trusted — the same reading `bounds` and `cellList` take of `cells`.
 */
export function rebuildStructureMirror(
  position: Position,
  industry: IndustryState
): void {
  invalidateMoves(position);
  const mirror = new Map<number, Color>();
  const health = new Map<number, number>();
  const walls = new Set<number>();
  let offFloor = false;
  for (const sq of structureSquares(industry)) {
    const structure = structureAt(industry, sq)!;
    mirror.set(sq, structure.owner);
    health.set(sq, structureHealthOf(structure));
    if (isObstruction(structure)) walls.add(sq);
    if (!hasCell(position, sq)) offFloor = true;
  }
  position.structures = mirror;
  position.structureHealth = health;
  position.obstructions = walls;
  position.belts = new Set();
  // And the belt parts (§31 S2), which stand in the same mirror.
  syncBeltMirror(position, industry);
  // **A save from before §31 S1** took a building's square out of `cells`;
  // a structure stands on one now, so the floor comes back under it.
  if (offFloor) {
    const cells = new Set(position.cells);
    for (const sq of mirror.keys()) cells.add(sq);
    setCells(position, cells);
  }
}

/**
 * Squares a run's outstanding objectives need to stay walkable.
 *
 * Empty outside a run and empty once an objective is done: a square you have
 * already stood on is a square you may build on afterwards.
 */
export function reservedSquares(state: {
  run?: { level?: { objectives: readonly { id: string; square?: number }[] } | null;
          objectives: readonly { id: string; done: boolean }[] } | null;
}): number[] {
  const level = state.run?.level;
  if (!level) return [];
  const done = new Set(
    (state.run?.objectives ?? []).filter((one) => one.done).map((one) => one.id)
  );
  const out: number[] = [];
  for (const objective of level.objectives) {
    if (objective.square === undefined || done.has(objective.id)) continue;
    out.push(objective.square);
  }
  return out;
}

/** What a piece standing here could take down, in square order. */
export function demolishTargets(
  position: Position,
  industry: IndustryState,
  by: number
): number[] {
  return neighbours(by)
    .filter((sq) => structureAt(industry, sq) !== undefined)
    .sort((a, b) => a - b);
}

/**
 * Whose pieces are standing next to this building and could take it down, in
 * a fixed order.
 *
 * Cardinals first, so the piece a player would point at — the one squarely
 * beside it — is the one a single click uses.
 */
export function demolishers(
  position: Position,
  at: number,
  owner: Color
): number[] {
  return neighbours(at).filter((sq) => {
    const piece = pieceAt(position, sq);
    return piece !== 0 && pieceColor(piece) === owner;
  });
}

/**
 * Stores hand on what they have been told to offer.
 *
 * **Run before anything accepts this tick**, which is the whole of PLAN
 * §14.4.4's rule that a store may never emit, in one tick, a unit it accepted
 * in that tick. Without it a row of stores is a teleporter: the unit arrives
 * at the first, is passed to the second in the same pass, and crosses the map
 * in a turn. Going first needs no snapshot and no extra state — anything a
 * store emits here demonstrably predates this tick's arrivals — and it is the
 * same trick belt lanes use when they resolve head-end backwards.
 *
 * A store has no *process* pass: it is a box with a policy, and the only
 * question is what it lets out.
 */
export function runStores(
  position: Position,
  industry: IndustryState,
  owner: Color,
  /** Optional: this tick's routing fields, so each is built once. */
  cache?: DemandCache
): void {
  for (const sq of structureSquares(industry)) {
    const structure = structureAt(industry, sq)!;
    if (structure.owner !== owner) continue;
    if (structure.offer === 0) continue;

    for (let sent = 0; sent < RULES.storeHandoffRate; sent++) {
      // Cheapest first, like every other thing that spends material: the good
      // metal is what you are trying not to let go of. **Past a unit nobody
      // takes** to the next kind that somebody does — a king with its coal
      // refusing a second one used to wall in the iron behind it (§33 I1).
      let index = -1;
      let target: ReturnType<typeof loadTarget> = null;
      for (const i of offerOrder(structure)) {
        target = loadTarget(position, industry, sq, owner, structure.stock[i], cache);
        if (target) {
          index = i;
          break;
        }
      }
      if (index < 0 || !target) break;

      const unit = structure.stock[index];

      structure.stock.splice(index, 1);
      const landed = target.machine
        ? deliverUnit(position, industry, target.square, unit)
        : pushUnit(industry, target.square, unit, "cargo");
      // Put it back rather than dropping it. A hand-off that fails at the last
      // step used to be how material quietly left the game.
      if (!landed) {
        structure.stock.splice(index, 0, unit);
        break;
      }
    }
  }
}

/**
 * One index per distinct unit this store is offering, cheapest grade first,
 * ties by stock order — the first of each kind, so a store of forty iron
 * asks the routing field once, not forty times.
 */
function offerOrder(structure: Structure): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  structure.stock.forEach((unit, i) => {
    if (!offersGrade(structure, unitGrade(unit)) || seen.has(unit)) return;
    seen.add(unit);
    out.push(i);
  });
  return out.sort((a, b) => unitGrade(structure.stock[a]) - unitGrade(structure.stock[b]) || a - b);
}

/**
 * Material held in a store, by grade — for the shelf and the badge.
 *
 * Not `bufferAtLeast`: a warehouse's whole point is what is in it, and the
 * player is choosing which taps to open per grade.
 */
export function stockByGrade(structure: Structure): number[] {
  const out = new Array<number>(GRADE_SLOTS).fill(0);
  for (const unit of structure.stock) out[unitGrade(unit)]++;
  return out;
}

/**
 * The same stock by **line and rung** (`line * GRADE_SLOTS + grade`).
 *
 * Two readings on purpose. The **tap panel** is grade-indexed because the
 * accept/offer masks are — a store's taps are five grades across every line,
 * and giving them a tap per line is the parked material-strip pass, not this.
 * Anything that *names* what is inside needs the line, or a warehouse of
 * cobble reports iron.
 */
export function stockBySlot(structure: Structure): number[] {
  const out = new Array<number>(LINE_COUNT * GRADE_SLOTS).fill(0);
  for (const unit of structure.stock) out[unitLine(unit) * GRADE_SLOTS + unitGrade(unit)]++;
  return out;
}

/** Seed a store's contents. Tests and scenarios only; never a command. */
export function fillStore(
  industry: IndustryState,
  at: number,
  grade: Grade,
  amount: number
): void {
  const structure = structureAt(industry, at);
  if (!structure) return;
  for (let i = 0; i < amount; i++) {
    if (structure.stock.length >= inventoryOf(structure.kind)) return;
    structure.stock.push(makeUnit(grade));
  }
}
